import { useState } from "react";
import { Volume2, Upload, Play, RotateCcw, Check, Music } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { uploadFile, fileUrl } from "@/lib/upload";
import { playDefault, PRESETS } from "@/lib/sounds";

const KINDS = [["spin", "Giro / gioco in corso"], ["reel_tick", "Slot: rulli che girano"], ["reel_stop", "Slot: stop di ogni rullo"], ["jackpot", "Slot: jackpot (3 uguali)"], ["win", "Vincita"], ["lose", "Non vincita"]];
const MUSIC = [
  { key: "lounge", label: "Lounge", desc: "Accordi morbidi e rilassati, 72 bpm", url: "/music/lounge.mp3" },
  { key: "pop", label: "Pop", desc: "Ritmo allegro e moderno, 118 bpm", url: "/music/pop.mp3" },
  { key: "natalizio", label: "Natalizio", desc: "Campanelle e melodia festiva", url: "/music/natalizio.mp3" },
];
let previewAudio;
const previewMusic = (url) => { previewAudio?.pause(); previewAudio = new Audio(url); previewAudio.volume = 0.6; previewAudio.play().catch(() => {}); setTimeout(() => previewAudio?.pause(), 12000); };

function Row({ kind, label, value, onChange, preset }) {
  const [prog, setProg] = useState(null);
  const src = fileUrl(value?.file_id) || value?.url;
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > (kind === "ambient" ? 8 : 2) * 1024 * 1024) return toast.error(kind === "ambient" ? "Il brano supera gli 8 MB" : "Il file audio supera i 2 MB");
    setProg(0);
    try {
      const doc = await uploadFile(f, `sound_${kind}`, setProg);
      onChange({ file_id: doc.id, url: null, filename: f.name });
      toast.success("Suono caricato");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProg(null);
    }
  };
  return (
    <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center" data-testid={`sound-row-${kind}`}>
      <div>
        <Label className="text-xs">{label}</Label>
        {value?.file_id ? (
          <p className="h-8 mt-1 px-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-xs flex items-center truncate" data-testid={`sound-file-${kind}`}>
            File caricato{value.filename ? `: ${value.filename}` : ""}
          </p>
        ) : (
          <Input value={value?.url || ""} onChange={(e) => onChange({ file_id: null, url: e.target.value || null })} placeholder="URL mp3/wav (vuoto = suono predefinito)" className="h-8 text-xs mt-1" data-testid={`sound-url-${kind}`} />
        )}
      </div>
      <label className="cursor-pointer mt-5" title="Carica MP3/WAV (max 2 MB)">
        <input type="file" accept="audio/mpeg,audio/wav,audio/x-wav,audio/ogg,audio/mp4,.mp3,.wav" onChange={pick} className="hidden" data-testid={`sound-upload-${kind}`} />
        <div className="h-8 px-2 rounded-md border border-border grid place-items-center text-xs hover:border-primary">{prog !== null ? `${prog}%` : <Upload className="h-3.5 w-3.5" />}</div>
      </label>
      <Button size="sm" variant="ghost" className="mt-5 h-8" onClick={() => (src ? new Audio(src).play() : playDefault(kind, preset))} data-testid={`sound-play-${kind}`} title={src ? "Ascolta il suono caricato" : "Ascolta il suono predefinito"}><Play className="h-3.5 w-3.5" /></Button>
      <Button size="sm" variant="ghost" className="mt-5 h-8 text-muted-foreground" disabled={!src} onClick={() => onChange(null)} data-testid={`sound-reset-${kind}`} title="Ripristina suono predefinito"><RotateCcw className="h-3.5 w-3.5" /></Button>
    </div>
  );
}

export default function SoundsEditor({ game, setGame }) {
  const sounds = game.sounds || {};
  return (
    <Card className="p-5 bg-card border-border space-y-3" data-testid="sounds-editor">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><Volume2 className="h-4 w-4" /> Effetti sonori</h3>
        <div className="flex items-center gap-2 text-xs"><span>Audio attivo</span><Switch data-testid="sound-enabled-switch" checked={game.sound_enabled !== false} onCheckedChange={(v) => setGame({ ...game, sound_enabled: v })} /></div>
      </div>
      <p className="text-xs text-muted-foreground">Scegli un set di suoni pronto oppure carica un MP3/WAV (max 2 MB) per ogni evento. Senza file vengono usati i suoni del set scelto; ↺ ripristina il predefinito. Ricorda di salvare.</p>
      <div className="grid gap-2 sm:grid-cols-3" data-testid="sound-presets">
        {Object.entries(PRESETS).map(([k, p]) => {
          const active = (game.sound_preset || "classico") === k;
          return (
            <button key={k} type="button" onClick={() => { setGame({ ...game, sound_preset: k }); playDefault("win", k); }} className={`rounded-xl border p-3 text-left transition-colors ${active ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`} data-testid={`sound-preset-${k}`}>
              <div className="flex items-center justify-between"><b className="text-sm">{p.label}</b>{active && <Check className="h-4 w-4 text-primary" />}</div>
              <p className="mt-0.5 text-[11px] text-muted-foreground">{p.desc}</p>
              <span className="mt-2 inline-flex items-center gap-1 text-[11px] text-primary"><Play className="h-3 w-3" /> Ascolta</span>
            </button>
          );
        })}
      </div>
      {KINDS.map(([k, l]) => (
        <Row key={k} kind={k} label={l} value={sounds[k]} preset={game.sound_preset} onChange={(v) => setGame({ ...game, sounds: { ...sounds, [k]: v } })} />
      ))}
      <div className="rounded-xl border border-border p-3 space-y-2" data-testid="ambient-music-box">
        <div className="flex items-center gap-2"><Music className="h-4 w-4 text-primary" /><b className="text-sm">Musica di sottofondo in attesa</b><span className="text-[11px] text-muted-foreground">(facoltativa · MP3 max 8 MB · in loop, si abbassa durante il gioco)</span></div>
        <Row kind="ambient" label="Brano" value={game.ambient} preset={game.sound_preset} onChange={(v) => setGame({ ...game, ambient: v })} />
        <div className="grid gap-2 sm:grid-cols-3" data-testid="music-library">
          {MUSIC.map((m) => {
            const active = game.ambient?.url === m.url;
            return (
              <div key={m.url} className={`flex items-center gap-2 rounded-lg border p-2 ${active ? "border-primary bg-primary/10" : "border-border"}`} data-testid={`music-${m.key}`}>
                <button type="button" onClick={() => setGame({ ...game, ambient: { file_id: null, url: m.url, filename: m.label } })} className="flex-1 text-left" data-testid={`music-pick-${m.key}`}>
                  <b className="block text-xs">{m.label}</b><span className="text-[10px] text-muted-foreground">{m.desc}</span>
                </button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => previewMusic(m.url)} data-testid={`music-play-${m.key}`} title="Ascolta"><Play className="h-3.5 w-3.5" /></Button>
                {active && <Check className="h-4 w-4 text-primary" />}
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground">Brani royalty-free inclusi, prodotti da Nouvelle Contest: usali liberamente sui totem. Oppure carica un tuo MP3 sopra.</p>
        <div className="flex items-center gap-3">
          <Label className="text-xs whitespace-nowrap">Volume {Math.round((game.ambient_volume ?? 0.35) * 100)}%</Label>
          <input type="range" min={0} max={100} value={Math.round((game.ambient_volume ?? 0.35) * 100)} onChange={(e) => setGame({ ...game, ambient_volume: Number(e.target.value) / 100 })} className="flex-1 accent-primary" data-testid="ambient-volume" />
        </div>
      </div>
    </Card>
  );
}
